import { catalogLookup, type CatalogEntry } from './extension-catalog';
import type { BrowserKind, DeviceRecord, InstalledExtension } from './types';

export interface DeviceInventory {
  device: Pick<DeviceRecord, 'id' | 'name' | 'browser'>;
  extensions: InstalledExtension[];
}

export interface ExtensionGroup {
  /** Stable identity shared by equivalent Chrome and Firefox builds. */
  key: string;
  name: string;
  catalog?: CatalogEntry;
  /** deviceId → the install on that device. */
  installs: Map<string, InstalledExtension>;
  chromeId?: string;
  firefoxId?: string;
  homepageUrl?: string;
  type: InstalledExtension['type'];
}

export interface InstallLink {
  url: string;
  /** `listing` links straight to the store page, `search` to a store search. */
  kind: 'listing' | 'search';
}

const NOISE = [
  /\bfor (google )?chrome\b/g,
  /\bfor (mozilla )?firefox\b/g,
  /\bbrowser extension\b/g,
  /\bextension\b/g,
  /\badd-?on\b/g,
  /[®™©]/g,
];

/** Normalised display name used to pair extensions not present in the catalog. */
export function normalizeExtensionName(name: string): string {
  let n = name.toLowerCase().replace(/\([^)]*\)/g, ' ');
  const first = n.split(/\s[-–—|:]\s|:\s/)[0]!;
  if (first.replace(/[^a-z0-9]/g, '').length >= 3) n = first;
  for (const re of NOISE) n = n.replace(re, ' ');
  return n.replace(/[^\p{L}\p{N}]+/gu, '');
}

export function extensionKey(browser: BrowserKind, ext: Pick<InstalledExtension, 'extId' | 'name'>): string {
  if (browser === 'chrome' || browser === 'firefox') {
    const entry = catalogLookup(browser, ext.extId);
    if (entry) return `cat:${entry.key}`;
  }
  const norm = normalizeExtensionName(ext.name);
  return norm ? `name:${norm}` : `id:${ext.extId}`;
}

export interface GroupOptions {
  /** Extension IDs to leave out (typically this app's own extension). */
  ignoreIds?: Iterable<string>;
  includeThemes?: boolean;
}

export function groupExtensions(inventories: DeviceInventory[], opts: GroupOptions = {}): ExtensionGroup[] {
  const ignore = new Set(opts.ignoreIds ?? []);
  const groups = new Map<string, ExtensionGroup>();
  for (const { device, extensions } of inventories) {
    if (device.browser === 'web') continue;
    for (const ext of extensions) {
      if (ignore.has(ext.extId)) continue;
      if (ext.type !== 'extension' && !(opts.includeThemes && ext.type === 'theme')) continue;
      const key = extensionKey(device.browser, ext);
      let g = groups.get(key);
      if (!g) {
        const catalog = key.startsWith('cat:') ? catalogLookup(device.browser, ext.extId) : undefined;
        g = {
          key,
          name: catalog?.name ?? ext.name,
          catalog,
          installs: new Map(),
          chromeId: catalog?.chromeId,
          firefoxId: catalog?.firefoxId,
          type: ext.type,
        };
        groups.set(key, g);
      }
      g.installs.set(device.id, ext);
      if (device.browser === 'chrome' && !g.chromeId) g.chromeId = ext.extId;
      if (device.browser === 'firefox' && !g.firefoxId) g.firefoxId = ext.extId;
      if (!g.homepageUrl && ext.homepageUrl) g.homepageUrl = ext.homepageUrl;
    }
  }
  return [...groups.values()].sort((a, b) => a.name.localeCompare(b.name));
}

/** Store page (or store search) where the user can install `group` in `browser`. */
export function installLink(group: ExtensionGroup, browser: BrowserKind): InstallLink | null {
  if (browser === 'chrome') {
    if (group.chromeId && /^[a-p]{32}$/.test(group.chromeId)) {
      return { url: `https://chromewebstore.google.com/detail/${group.chromeId}`, kind: 'listing' };
    }
    return { url: `https://chromewebstore.google.com/search/${encodeURIComponent(group.name)}`, kind: 'search' };
  }
  if (browser === 'firefox') {
    if (group.catalog?.amoSlug) {
      return { url: `https://addons.mozilla.org/firefox/addon/${group.catalog.amoSlug}/`, kind: 'listing' };
    }
    if (group.firefoxId) {
      // AMO resolves add-on GUIDs in listing URLs.
      return { url: `https://addons.mozilla.org/firefox/addon/${encodeURIComponent(group.firefoxId)}/`, kind: 'listing' };
    }
    return { url: `https://addons.mozilla.org/firefox/search/?q=${encodeURIComponent(group.name)}`, kind: 'search' };
  }
  return null;
}

/** Groups installed on at least one other device but not on `deviceId`. */
export function missingOn(groups: ExtensionGroup[], deviceId: string): ExtensionGroup[] {
  return groups.filter((g) => !g.installs.has(deviceId) && g.installs.size > 0);
}

export type CoverageStatus = 'everywhere' | 'partial';

export function coverage(group: ExtensionGroup, deviceIds: string[]): CoverageStatus {
  return deviceIds.every((id) => group.installs.has(id)) ? 'everywhere' : 'partial';
}
